/**
 * Backend-API client for AP (B2B guest) data access.
 *
 * B2B guests cannot read SharePoint via delegated Graph tokens (SharePoint
 * refuses them regardless of permission). So AP flows go through our own
 * same-origin `/api`, which performs every SharePoint operation app-only and
 * enforces per-AP isolation server-side. Internal staff keep using the direct
 * Graph client; the generic service functions branch on `isApiClient(client)`.
 *
 * This object is passed around with the same `Client` type the Graph SDK uses
 * (via a cast in useGraphClient) so the feature services need no signature
 * changes — they just forward whichever client they were given.
 */

import { GraphApiError } from './graphError';

export interface ApiClient {
  readonly __api: true;
  /** POST JSON to `/api{path}` with the user's bearer token; returns parsed JSON. */
  post<T>(path: string, body: unknown): Promise<T>;
}

export function isApiClient(client: unknown): client is ApiClient {
  return !!client && (client as { __api?: unknown }).__api === true;
}

/** Base path for the managed API (same origin). Overridable for local dev. */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export function createApiClient(getToken: () => Promise<string>): ApiClient {
  return {
    __api: true,
    async post<T>(path: string, body: unknown): Promise<T> {
      const token = await getToken();
      let res: Response;
      try {
        res = await fetch(`${API_BASE}${path}`, {
          method: 'POST',
          headers: {
            // Azure Static Web Apps overwrites Authorization with its own
            // platform token before the managed function sees it, so the user
            // token travels in a custom header that SWA passes through.
            'x-ap-token': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body ?? {}),
        });
      } catch (e) {
        throw new GraphApiError(0, `Network error calling ${path}: ${(e as Error).message}`);
      }

      const text = await res.text();
      let data: unknown = null;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = null;
        }
      }

      if (!res.ok) {
        // The body is `{ error: { message, code?, ...details? } }`. `message` is
        // read exactly as it always was — every existing consumer depends on it
        // and nothing about that path changes. `code` is the addition: without
        // it the server's NAMED reason for a refusal (AP_INACTIVE,
        // AP_IDENTITY_NO_APCODE, …) stops at the network boundary and the app
        // can only say "something went wrong". The extra `details` keys stay
        // unread here on purpose — they are per-code and belong to the caller
        // that knows the code.
        const body = (data as { error?: { message?: string; code?: unknown } } | null)?.error;
        const message = body?.message ?? res.statusText ?? 'Request failed';
        const code = typeof body?.code === 'string' ? body.code : '';
        const requestId = res.headers.get('request-id') ?? '';
        throw new GraphApiError(res.status, message, requestId, code);
      }

      return data as T;
    },
  };
}
